<template>
  <div class="mb-5">
    <!-- DRAFT: pick from the full list of the source establishment's available equipment -->
    <div v-if="isDraft">
      <div class="col-12 d-flex flex-wrap align-items-center justify-content-between mb-3">
        <base-input
          class="mb-0 flex-grow-1 mr-3"
          :placeholder="$t('COMMON.SEARCH_HINT.NAME_OR_CODE')"
          prepend-icon="fas fa-search"
          v-model="search"
          @input="onSearch"
        >
        </base-input>
        <span class="text-muted text-nowrap">
          {{ $t("EQUIPMENT_TRANSFERS.SELECTED_COUNT", { count: items.length }) }}
        </span>
      </div>

      <el-table
        class="table-responsive align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="availableEquipments"
        v-loading="loadingEquipments"
      >
        <div slot="empty">
          {{ $t("EQUIPMENT_TRANSFERS.NO_AVAILABLE_EQUIPMENT") }}
        </div>

        <el-table-column :label="$t('EQUIPMENT_TRANSFERS.INCLUDE')" width="90" align="center">
          <template v-slot="{ row }">
            <el-checkbox
              :value="isIncluded(row)"
              @change="(checked) => toggleEquipment(row, checked)"
            />
          </template>
        </el-table-column>

        <el-table-column :label="$t('EQUIPMENT_TRANSFERS.EQUIPMENT')" min-width="220">
          <template v-slot="{ row }">
            <div>
              <span>{{ row.name }}</span>
              <div class="text-muted" v-if="row.code">
                {{ $t("COMMON.CODE") }} : {{ row.code }}
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column :label="$t('EQUIPMENT_TRANSFERS.BRAND')" min-width="140">
          <template v-slot="{ row }">
            {{ row.brand ? row.brand : "-" }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('EQUIPMENT_TRANSFERS.SERIAL_NUMBER')"
          min-width="160"
        >
          <template v-slot="{ row }">
            {{ row.serial_number ? row.serial_number : "-" }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('EQUIPMENT_TRANSFERS.QUANTITY')"
          width="140"
          align="center"
        >
          <template v-slot="{ row }">
            <base-input
              class="mb-0"
              type="number"
              min="1"
              :disabled="!isIncluded(row)"
              v-model="quantities[row.id]"
              @change="() => onQuantityChanged(row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- NON-DRAFT: read-only list of the transferred equipment + return actions -->
    <el-table
      v-else
      class="table-responsive align-items-center table-flush"
      header-row-class-name="thead-light"
      :data="items"
    >
      <div slot="empty">
        {{ $t("EQUIPMENT_TRANSFERS.ADD_ITEMS_FIRST") }}
      </div>

      <el-table-column :label="$t('EQUIPMENT_TRANSFERS.EQUIPMENT')" min-width="220">
        <template v-slot="{ row }">
          <div v-if="row.equipment">
            <a href="#" @click.prevent="goToEquipment(row.equipment)">
              {{ row.equipment.name }}
            </a>
            <div class="text-muted" v-if="row.equipment.code">
              {{ $t("COMMON.CODE") }} : {{ row.equipment.code }}
            </div>
          </div>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column :label="$t('EQUIPMENT_TRANSFERS.BRAND')" min-width="140">
        <template v-slot="{ row }">
          {{ row.equipment && row.equipment.brand ? row.equipment.brand : "-" }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('EQUIPMENT_TRANSFERS.SERIAL_NUMBER')"
        min-width="160"
      >
        <template v-slot="{ row }">
          {{
            row.equipment && row.equipment.serial_number
              ? row.equipment.serial_number
              : "-"
          }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('EQUIPMENT_TRANSFERS.QUANTITY')"
        class-name="number-column"
        label-class-name="number-column"
        min-width="100"
      >
        <template v-slot="{ row }">
          {{ row.quantity }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="isTemporary"
        :label="$t('COMMON.STATUS')"
        min-width="130"
      >
        <template v-slot="{ row }">
          <badge :type="row.is_returned ? 'success' : 'warning'">
            {{
              row.is_returned
                ? $t("EQUIPMENT_TRANSFERS.STATUS_RETURNED")
                : $t("EQUIPMENT_TRANSFERS.ON_LOAN")
            }}
          </badge>
        </template>
      </el-table-column>

      <el-table-column min-width="140" align="center" fixed="right">
        <div slot-scope="{ row }" class="table-actions">
          <base-button
            v-if="canReturnItem(row)"
            type="warning"
            size="sm"
            @click="returnItem(row)"
          >
            {{ $t("EQUIPMENT_TRANSFERS.RETURN") }}
          </base-button>
        </div>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { Table, TableColumn, Checkbox } from "element-ui";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import defaultEquipmentTransferItem from "../defaultEquipmentTransferItem";
import {
  EQUIPMENT_TRANSFER_STATUS_ACCEPTED,
  EQUIPMENT_TRANSFER_STATUS_PARTIALLY_RETURNED,
  EQUIPMENT_TRANSFER_TYPE_TEMPORARY,
} from "@/constants/equipmentTransfers";

export default {
  name: "EquipmentTransferItemsTable",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Checkbox.name]: Checkbox,
  },
  props: {
    transfer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      search: "",
      availableEquipments: [],
      quantities: {},
      loadingEquipments: false,
    };
  },
  computed: {
    isDraft() {
      return (this.transfer.status || "DRAFT") === "DRAFT";
    },
    isTemporary() {
      return this.transfer.transfer_type === EQUIPMENT_TRANSFER_TYPE_TEMPORARY;
    },
    items() {
      return this.transfer.items || [];
    },
    sourceEstablishmentId() {
      return this.transfer.sourceEstablishment
        ? this.transfer.sourceEstablishment.id
        : null;
    },
  },
  created() {
    this.onSearch = debounce(this.loadAvailableEquipments, 400);
    if (this.isDraft) {
      this.loadAvailableEquipments();
    }
  },
  methods: {
    async loadAvailableEquipments() {
      if (!this.isDraft || !this.sourceEstablishmentId) {
        this.availableEquipments = [];
        return;
      }
      this.loadingEquipments = true;
      try {
        const params = {
          sort: "name",
          filter: {
            establishment: this.sourceEstablishmentId,
            loan_status: "AVAILABLE",
            ...(this.search ? { search: encodeURIComponent(this.search) } : {}),
          },
          page: { number: 1, size: 100 },
        };
        await this.$store.dispatch("equipments/list", params);
        const list = this.$store.getters["equipments/list"];
        this.availableEquipments = Array.isArray(list) ? list : [];
        this.syncQuantities();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loadingEquipments = false;
      }
    },
    syncQuantities() {
      this.availableEquipments.forEach((equipment) => {
        const item = this.itemFor(equipment);
        const value = item ? item.quantity : this.quantities[equipment.id] || 1;
        this.$set(this.quantities, equipment.id, value);
      });
    },
    itemFor(equipment) {
      return this.items.find((item) => {
        const equipmentId = item.equipment ? item.equipment.id : item.equipment_id;
        return equipmentId == equipment.id;
      });
    },
    isIncluded(equipment) {
      return !!this.itemFor(equipment);
    },
    toggleEquipment(equipment, checked) {
      if (checked) {
        const parsedQty = parseFloat(this.quantities[equipment.id]);
        const itemData = cloneDeep(defaultEquipmentTransferItem);
        itemData.transfer = { type: "equipment-transfers", id: this.transfer.id };
        itemData.equipment = { type: "equipments", id: equipment.id };
        itemData.quantity = isNaN(parsedQty) || parsedQty < 1 ? 1 : parsedQty;
        this.$emit("onSubmitTransferItem", itemData);
      } else {
        const item = this.itemFor(equipment);
        if (item) {
          this.$emit("onDeleteTransferItem", item.id);
        }
      }
    },
    onQuantityChanged(equipment) {
      const item = this.itemFor(equipment);
      if (!item) {
        return;
      }
      const parsedQty = parseFloat(this.quantities[equipment.id]);
      const itemData = cloneDeep(defaultEquipmentTransferItem);
      itemData.id = item.id;
      itemData.transfer = { type: "equipment-transfers", id: this.transfer.id };
      itemData.equipment = { type: "equipments", id: equipment.id };
      itemData.quantity = isNaN(parsedQty) || parsedQty < 1 ? 1 : parsedQty;
      this.$emit("onSubmitTransferItem", itemData);
    },
    goToEquipment(equipment) {
      this.$router.push({
        name: "View Equipment",
        params: { id: equipment.id },
      });
    },
    canReturnItem(row) {
      return (
        this.isTemporary &&
        !row.is_returned &&
        [
          EQUIPMENT_TRANSFER_STATUS_ACCEPTED,
          EQUIPMENT_TRANSFER_STATUS_PARTIALLY_RETURNED,
        ].includes(this.transfer.status) &&
        this.$currentUserCan(this.$permissions.PERM_RETURN_EQUIPMENT_TRANSFERS)
      );
    },
    returnItem(row) {
      this.$emit("onReturnItem", row.id);
    },
  },
  watch: {
    sourceEstablishmentId() {
      if (this.isDraft) {
        this.loadAvailableEquipments();
      }
    },
    items() {
      this.syncQuantities();
    },
  },
};
</script>
